/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, { useEffect, useRef, useState } from "react";
import {
  CompositeDecorator,
  ContentState,
  convertFromRaw,
  convertToRaw,
  Editor,
  EditorState,
  Modifier,
  SelectionState,
  AtomicBlockUtils,
} from "draft-js";
import * as linkify from "linkifyjs";
import {
  Button,
  Modal,
  ModalHeader,
  OverlayTrigger,
  Popover,
} from "react-bootstrap";
import TokenForm from "./TokenForm";
import EditorButtons from "./EditorButtons";
import EditorPreview from "./EditorPreview";
import MyScroll from "../../Common/MyScroll";
import Shortlinks from "../../Common/Shortlinks";
import MyModal from "../../Common/MyModal";
import HelpButton from "../../HelpContent/HelpButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import AiModal from "../../Broadcast/AiModal";

// renders the entity as a token
export function TokenSpan({
  offsetkey,
  children,
  contentState,
  entityKey,
  setEditedToken,
  setEditorState,
  blockKey,
  start,
  end,
}) {
  const token = contentState.getEntity(entityKey).getData();

  return (
    <span
      data-offset-key={offsetkey}
      data-token-uuid={token.UUID}
      className="btn btn-colored rounded-pill py-1 px-2 align-baseline"
      onClick={() => setEditedToken(token)}
      onDragStart={(e) => e.dataTransfer.setData("text", e.target.innerText)}
      onMouseDown={() => {
        const newSelection = new SelectionState({
          anchorKey: blockKey,
          anchorOffset: start,
          focusKey: blockKey,
          focusOffset: end,
          hasFocus: false,
          isBackward: false, // isBackward = (focusOffset < anchorOffset)
        });
        setEditorState((prev) =>
          EditorState.forceSelection(prev, newSelection)
        );
      }}
    >
      {children}
    </span>
  );
}

export default function TokenEditor({
  label,
  rawEditorState,
  onChange,
  style,
  showButtons,
  preview,
  initialText,
  tokenTags,
  inputArgumentsFilter,
  inputArguments,
  showAiButton = true,
  showShortlinksButton = true,
  showTokens = true,
}) {
  const [showShortLinks, setShowShortLinks] = useState(false);
  const [editedToken, setEditedToken] = useState(null);
  const [editorState, setEditorState] = useState(null);
  const [shortlinks, setShortlinks] = useState({
    startingWith: [],
    expiresAfterVisits: 0,
    expireAfterDays: 0,
    expires: "",
    isProtected: false,
    password: "",
  });

  // refs for divs
  const editorRef = useRef();
  const htmlDivRef = useRef();

  // strategy for decorator
  function getEntityStrategy(mutability) {
    return (contentBlock, callback, contentState) => {
      contentBlock.findEntityRanges((character) => {
        const entityKey = character.getEntity();
        if (entityKey === null) {
          return false;
        }
        return contentState.getEntity(entityKey).getMutability() === mutability;
      }, callback);
    };
  }

  function getLinksStrategy(contentBlock, callback, contentState) {
    const text = contentBlock.getText();
    const links = linkify.find(text);
    links.forEach((link) => {
      callback(link.start, link.end);
    });
  }

  function getShortLinksStrategy(contentBlock, callback, contentState) {
    if (!shortlinks.startingWith || shortlinks.startingWith.length === 0)
      return;
    const regex = new RegExp(shortlinks.startingWith.join("|"), "gi");
    const text = contentBlock.getText();
    let matchArr, start;
    while ((matchArr = regex.exec(text)) !== null) {
      start = matchArr.index;
      callback(start, start + matchArr[0].length);
    }
  }

  // scans the blocks based on the above strategy
  const decorator = new CompositeDecorator([
    {
      strategy: getEntityStrategy("IMMUTABLE"),
      component: TokenSpan,
      props: {
        setEditedToken,
        setEditorState,
      },
    },
    {
      strategy: getShortLinksStrategy,
      component: (props) => (
        <button
          className="btn btn-secondary p-0 px-2 m-0 align-baseline"
          title="Manage shortlinks"
          onClick={() => setShowShortLinks(true)}
        >
          {props.children} <FontAwesomeIcon icon={faLink} />
        </button>
      ),
    },
    {
      strategy: getLinksStrategy,
      component: (props) => (
        <button
          className="btn btn-link p-0 m-0 align-baseline"
          title="You can use shortlinks"
          onClick={() => setShowShortLinks(true)}
        >
          {props.children}
        </button>
      ),
    },
  ]);

  // update decorator when shortlinks change
  useEffect(() => {
    console.log("shortlinks chaged", shortlinks);
    if (!editorState) return;
    setEditorState(
      EditorState.set(editorState, {
        decorator,
      })
    );
  }, [shortlinks]);

  // inserts a text(or dragged text) in the current selected position
  const insertEntity = (
    token,
    type,
    selection = editorState.getSelection()
  ) => {
    const text = `${token._label ? `${token._label}: ` : ""}${
      token._value || token.Value
    }`;
    const contentState = editorState.getCurrentContent();
    let contentStateWithEntity = contentState.createEntity(
      type,
      "IMMUTABLE",
      token
    );

    contentStateWithEntity = Modifier.replaceText(
      contentStateWithEntity,
      selection,
      text,
      null,
      contentStateWithEntity.getLastCreatedEntityKey()
    );

    const newEditorState = EditorState.forceSelection(
      EditorState.set(editorState, {
        currentContent: contentStateWithEntity,
      }),
      // move cursor to the end of the inserted text
      selection.merge({
        focusOffset: selection.getAnchorOffset() + text.length,
        anchorOffset: selection.getAnchorOffset() + text.length,
      })
    );
    setEditorState(newEditorState);
    // setDraggedText(null);
  };

  const getTokens = () => {
    const content = editorState.getCurrentContent();
    const entities = [];
    content.getBlocksAsArray().forEach((block) => {
      let selectedEntity = null;
      block.findEntityRanges(
        (character) => {
          if (character.getEntity() !== null) {
            const entity = content.getEntity(character.getEntity());
            if (entity.getType()) {
              selectedEntity = {
                entityKey: character.getEntity(),
                blockKey: block.getKey(),
                entity: content.getEntity(character.getEntity()),
              };
              return true;
            }
          }
          return false;
        },
        (start, end) => {
          if (
            !entities.find((ent) => ent.entityKey === selectedEntity.entityKey)
          ) {
            entities.push({ ...selectedEntity, start, end });
          }
        }
      );
    });
    return entities.map((ent) => ent.entity.getData());
  };

  const links = [];
  editorState
    ?.getCurrentContent()
    .getBlocksAsArray()
    .forEach((block) => {
      const blockLinks = linkify.find(block.getText());
      links.splice(0, 0, ...blockLinks);
    });

  // selects an entity as whole
  const selectEntity = (newEditorState) => {
    const currentContent = newEditorState.getCurrentContent();
    const selection = newEditorState.getSelection();
    // if (!selection.isCollapsed()) {
    const startKey = selection.getStartKey();
    const startOffset = selection.getStartOffset();
    const endOffset = selection.getEndOffset();
    const block = currentContent.getBlockForKey(startKey);
    const entityKey = block.getEntityAt(startOffset);
    let newSelection;
    // iterate through entities in block
    block.findEntityRanges(
      // check if the current entity is the selected one
      (character) =>
        character.entity !== null && character.entity === entityKey,
      // if entity is the selected one, update SelectionState
      // to include the whole of entity
      (start, end) => {
        if (start < startOffset && end > endOffset) {
          newSelection = new SelectionState({
            anchorKey: block.key,
            anchorOffset: start,
            focusKey: block.key,
            focusOffset: end,
            hasFocus: false,
            isBackward: false, // isBackward = (focusOffset < anchorOffset)
          });
          setEditorState(
            EditorState.forceSelection(newEditorState, newSelection)
          );
        }
      }
    );
    // }
  };

  // focus on the editor (used when wrapping div is clicked)
  const focus = () => {
    editorRef.current.focus();
  };

  // handle editor state change
  const handleChange = (newEditorState) => {
    setEditorState(newEditorState);
    // select entity as whole
    selectEntity(newEditorState);
  };

  // handle drag and drop (workaround for drop in wrong position issue)
  const handleDrop = (targetSelection, dataTransfer, isInternal) => {
    if (isInternal === "internal") {
      const text = dataTransfer.getText();
      let content = editorState.getCurrentContent();
      const blockKey = editorState.getSelection().getStartKey();

      // if not the same block no need for a fix
      if (targetSelection.getStartKey() !== blockKey) {
        return false;
      }

      const sourceStart = editorState.getSelection().getStartOffset();
      let targetStart = targetSelection.getStartOffset();
      let targetEnd = targetSelection.getEndOffset();

      // check if target comes after source
      if (targetStart > sourceStart) {
        targetStart -= text.length;
        targetEnd -= text.length;
      }

      const newSelection = new SelectionState({
        anchorKey: blockKey,
        anchorOffset: targetStart,
        focusKey: blockKey,
        focusOffset: targetEnd,
        hasFocus: false,
        isBackward: false, // isBackward = (focusOffset < anchorOffset)
      });

      content = Modifier.moveText(
        content,
        editorState.getSelection(),
        newSelection
      );

      setEditorState(
        EditorState.push(editorState, content, "change-block-data")
      );
      return true;
    }
    return false;
  };

  // update the editorState in the message when editorState changes
  useEffect(() => {
    if (editorState) {
      onChange({
        rawEditorState: convertToRaw(editorState.getCurrentContent()),
        HTMLtext: htmlDivRef.current?.querySelector("[data-contents='true']")
          ?.innerHTML,
        tokens: getTokens(),
        plainText: editorState.getCurrentContent().getPlainText(),
        links,
        shortlinks,
      });
    }
  }, [editorState, shortlinks]);

  // load editor state from rawEditorState
  useEffect(() => {
    if (rawEditorState) {
      setEditorState(
        EditorState.createWithContent(convertFromRaw(rawEditorState), decorator)
      );
    } else {
      setEditorState(
        EditorState.createWithContent(
          ContentState.createFromText(initialText || ""),
          decorator
        )
      );
    }
  }, []);

  const Media = (props) => {
    const entityKey = props.block.getEntityAt(0);
    const entity = props.contentState.getEntity(entityKey);
    const { src, width } = entity.getData();
    const type = entity.getType();

    let media;
    if (type === "image") {
      media = (
        <div
          style={{
            width,
            resize: "horizontal",
            overflow: "auto",
          }}
          onMouseUp={(e) =>
            props.contentState.mergeEntityData(entityKey, {
              width: e.target.scrollWidth,
            })
          }
        >
          <img
            src={src}
            alt=""
            style={{
              width: "100%",
            }}
          />
        </div>
      );
    }

    return media;
  };

  const customBlockRenderer = (block) => {
    if (block.getType() === "atomic") {
      return {
        component: Media,
        editable: false,
      };
    }

    return null;
  };

  const insertImage = () => {
    const urlValue = prompt("Please enter image URL");
    if (!urlValue) {
      return;
    }
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      "image",
      "IMMUTABLE",
      { src: urlValue, width: 300 }
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(editorState, {
      currentContent: contentStateWithEntity,
    });
    setEditorState(
      AtomicBlockUtils.insertAtomicBlock(newEditorState, entityKey, " ")
    );
  };

  return (
    editorState && (
      <div>
        {label && <label className="form-label">{label}</label>}

        {!preview && (
          <>
            <div className="">
              {(showTokens || showAiButton || showShortlinksButton) && (
                <div className="d-flex flex-wrap my-2 bg-purple-gradient p-2 rounded-3 gap-1">
                  {showShortlinksButton && (
                    <button
                      className="btn btn-sm fw-bold bg-icon px-2 py-0"
                      onClick={() => setShowShortLinks(true)}
                    >
                      <span className="">
                        SHORT LINKS <FontAwesomeIcon icon={faLink} />
                        {links.length > 0 && (
                          <span className="badge bg-secondary ms-2">
                            {links.length}
                          </span>
                        )}
                      </span>
                    </button>
                  )}
                  {showTokens && (
                    <TokenForm
                      tags={tokenTags}
                      token={editedToken}
                      onClose={(newToken) => {
                        if (
                          newToken?.Value ||
                          [
                            "survey",
                            "workflow_name",
                            "assets",
                            "events",
                          ].includes(newToken?.Tag)
                        ) {
                          insertEntity(newToken, "TOKEN");
                        }
                        setEditedToken(null);
                      }}
                      inputArgumentsFilter={inputArgumentsFilter}
                      inputArguments={inputArguments}
                    />
                  )}
                  {showAiButton && (
                    <div className="ms-auto">
                      <AiModal
                        editorState={editorState}
                        setEditorState={setEditorState}
                      />
                    </div>
                  )}
                </div>
              )}
              {showButtons && (
                <EditorButtons
                  editorState={editorState}
                  setEditorState={setEditorState}
                  insertImage={insertImage}
                />
              )}
            </div>
            <div
              className="bg-white rounded-3 p-3"
              onClick={focus}
              onFocus={focus}
              onDragOver={focus}
              onDrop={(e) => e.stopPropagation()}
              ref={htmlDivRef}
              style={style}
            >
              <Editor
                editorState={editorState}
                onChange={handleChange}
                ref={editorRef}
                handleDrop={handleDrop}
                blockRendererFn={customBlockRenderer}
              />
            </div>
          </>
        )}
        {preview === "sms" && (
          <EditorPreview
            contentState={editorState.getCurrentContent()}
            previewType="sms"
          />
        )}
        {preview === "email" && (
          <EditorPreview
            contentState={editorState.getCurrentContent()}
            previewType="email"
          />
        )}
        <Modal
          show={showShortLinks}
          onHide={() => setShowShortLinks(false)}
          size="lg"
          contentClassName="bg-purple-gradient"
        >
          <ModalHeader closeButton>Shortlinks Settings </ModalHeader>
          <Shortlinks
            links={links}
            shortlinks={shortlinks}
            setShortlinks={setShortlinks}
          />
        </Modal>
      </div>
    )
  );
}
